import { Injectable, inject } from '@angular/core';
import { State, Action, StateContext, Store } from '@ngxs/store';
import {
  FormResetAction,
  UpdateMbbFormValidStatusAction,
  UpdateVoiceFormValidStatusAction,
  AddVoiceSubscriptionForMobilephoneToFormStateAction,
  UpdateFormHasBeenInView,
  TradeInFormResetAction,
  AddVoiceSubscriptionForAccessoryToFormStateAction,
  ProductFormResetAction,
  UpdateCustomerValidationFormAction,
  UpdateCustomerAllowanceFormAction,
} from './form.actions';
import { UpdateFormValue, UpdateFormStatus } from '@ngxs/form-plugin';
import { SubscriptionComponent } from '../../modules/subscription/components/subscription.component';
import { MbbComponent } from '../../modules/mbb/components/mbb/mbb.component';
import { FormBuilder } from '@angular/forms';
import { FormStatus } from '../../models/form-status.enum';
import { SimCardValidator } from '../../validators/async-sim-card-validator';
import { SimCardType } from '../../models/simcard.model';
import { CustomerService } from '../../modules/details/services/customer.service';
import { FormStateModel, TradeInConditionModel, TradeInFormModel } from './form.types';
import { FORM_STATE_TOKEN } from './form.token';
import { patch } from '@ngxs/store/operators';
import { VerificationType } from '@app/sales/modules/details/constants/Verification';

export const initialTradeInFormState = {
  model: {
    specifications: {
      model: {
        imei: '',
        storage: '',
      },
      dirty: false,
      status: '',
      errors: {},
    },
    condition: {} as TradeInConditionModel,
  },
  dirty: false,
  status: '',
  errors: {},
  hasBeenInView: false,
} as TradeInFormModel;

const initialCustomerAllowanceFormState = {
  model: {},
  dirty: false,
  status: '',
  errors: {},
  hasBeenInView: false,
};

const initialSubscriptionFormState = {
  model: {
    list: [],
  },
  dirty: false,
  status: '',
  errors: {},
  hasBeenInView: false,
};

const initialCustomerContactFormState = {
  model: {},
  dirty: false,
  status: '',
  errors: {},
  hasBeenInView: false,
};

const initialCustomerValidationFormState = {
  model: {},
  dirty: false,
  status: '',
  errors: {},
  hasBeenInView: false,
};

const initialMbbFormState = {
  model: {
    list: [],
  },
  dirty: false,
  status: '',
  errors: {},
  hasBeenInView: false,
};

export const initialFormState = {
  customerAllowanceForm: initialCustomerAllowanceFormState,
  subscriptionForm: initialSubscriptionFormState,
  customerContactForm: initialCustomerContactFormState,
  customerValidationForm: initialCustomerValidationFormState,
  mbbForm: initialMbbFormState,
  tradeInForm: initialTradeInFormState,
};

export const initialCompetitionFormState = {
  createCompetitionForm: {
    model: {},
    dirty: false,
    status: '',
    errors: {},
    hasBeenInView: false,
  },
};

@State<FormStateModel>({
  name: FORM_STATE_TOKEN,
  defaults: { ...initialFormState, ...initialCompetitionFormState },
})
@Injectable()
export class FormState {
  private formBuilder = inject(FormBuilder);
  private store = inject(Store);
  private customerService = inject(CustomerService);

  @Action(AddVoiceSubscriptionForMobilephoneToFormStateAction)
  public addVoiceSubscriptionToFormState(
    { dispatch, getState }: StateContext<FormStateModel>,
    { voiceProduct }: AddVoiceSubscriptionForMobilephoneToFormStateAction
  ) {
    const existingState = getState();
    const newVoiceSubscriptionItem = SubscriptionComponent.getNewVoiceProductFormState(voiceProduct);

    dispatch(
      new UpdateFormValue({
        path: 'formState.subscriptionForm',
        value: {
          list: [...existingState.subscriptionForm.model.list, newVoiceSubscriptionItem],
        },
      })
    );
    dispatch(new UpdateFormStatus({ path: 'formState.subscriptionForm', status: FormStatus.INVALID }));
  }

  @Action(AddVoiceSubscriptionForAccessoryToFormStateAction)
  public addVoiceSubscriptionForAccessoryToFormState(
    { dispatch, getState }: StateContext<FormStateModel>,
    { voiceProduct }: AddVoiceSubscriptionForAccessoryToFormStateAction
  ) {
    const existingState = getState();
    const newVoiceSubscriptionItem = SubscriptionComponent.getNewVoiceProductFormState(voiceProduct);

    dispatch(
      new UpdateFormValue({
        path: 'formState.subscriptionForm',
        value: {
          list: [...existingState.subscriptionForm.model.list, newVoiceSubscriptionItem],
        },
      })
    );
    dispatch(new UpdateFormStatus({ path: 'formState.subscriptionForm', status: FormStatus.INVALID }));
  }

  @Action(UpdateVoiceFormValidStatusAction)
  public setVoiceFormValidStatus(
    { dispatch }: StateContext<FormStateModel>,
    {
      products,
      formState,
      formBuilder,
      simService,
      subscriptionService,
      resetSubscriptionForm = false,
    }: UpdateVoiceFormValidStatusAction
  ) {
    if (resetSubscriptionForm) {
      dispatch(new UpdateFormStatus({ path: 'formState.subscriptionForm', status: FormStatus.VALID }));
    }

    if (
      SubscriptionComponent.buildFormFromExistingState(
        products,
        formState,
        formBuilder,
        this.store,
        SimCardValidator(simService, SimCardType.Voice),
        subscriptionService,
        this.customerService
      ).valid
    ) {
      dispatch(new UpdateFormStatus({ path: 'formState.subscriptionForm', status: FormStatus.VALID }));
    }
  }

  @Action(UpdateMbbFormValidStatusAction)
  public setMbbFormValidStatusValid(
    { dispatch }: StateContext<FormStateModel>,
    { formState, formBuilder, simService, resetMbbForm }: UpdateMbbFormValidStatusAction
  ) {
    if (resetMbbForm) {
      dispatch(new UpdateFormStatus({ path: 'formState.mbbForm', status: FormStatus.VALID }));
    }
    if (
      MbbComponent.buildFormFromExistingState(
        formState,
        formBuilder,
        SimCardValidator(simService, SimCardType.Mbb),
        this.store
      ).valid
    ) {
      dispatch(new UpdateFormStatus({ path: 'formState.mbbForm', status: FormStatus.VALID }));
    }
  }

  @Action(FormResetAction)
  public resetState({ patchState }: StateContext<FormStateModel>) {
    patchState(initialFormState);
  }

  @Action(TradeInFormResetAction)
  public resetTradeInFormState({ patchState }: StateContext<FormStateModel>) {
    patchState({ tradeInForm: initialTradeInFormState });
  }

  @Action(ProductFormResetAction)
  public resetProductFormState({ patchState }: StateContext<FormStateModel>) {
    patchState({
      tradeInForm: initialTradeInFormState,
      subscriptionForm: initialSubscriptionFormState,
      mbbForm: initialMbbFormState,
    });
  }

  @Action(UpdateFormHasBeenInView)
  updateFormHasBeenInView(ctx: StateContext<FormStateModel>, action: UpdateFormHasBeenInView) {
    const { path } = action;

    // We are only interested in the state name, not the path because we know that the path is always in the form of 'formState.stateName'
    const stateName = path.split('.')[1];
    ctx.setState(
      patch({
        [stateName]: patch({
          hasBeenInView: true,
        }),
      })
    );
  }

  @Action(UpdateCustomerValidationFormAction)
  updateCustomerValidationForm(ctx: StateContext<FormStateModel>, action: UpdateCustomerValidationFormAction) {
    ctx.patchState({
      customerValidationForm: {
        ...ctx.getState().customerValidationForm,
        model: {
          verificationType: action.payload.type,
          ...(action.payload.type === VerificationType.TwoFA ? { verificationCode: action.payload.verificationCode } : {}),
        },
      },
    });
  }

  @Action(UpdateCustomerAllowanceFormAction)
  updateCustomerAllowanceForm(ctx: StateContext<FormStateModel>, action: UpdateCustomerAllowanceFormAction) {
    const { firstName, lastName, ssn } = action;
    ctx.patchState({
      customerAllowanceForm: {
        ...ctx.getState().customerAllowanceForm,
        model: {
          firstname: firstName,
          lastname: lastName,
          ssn,
        },
      },
    });
  }
}
